import cn from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { trackEvent } from 'helpers/analytics';
import { fetchProductReviews, upvoteReview } from 'actions/productDetail';
import { parseAndCleanHtmlToReact } from 'helpers/HtmlHelpers';
import { Loader } from 'components/Loader';
import MartyLink from 'components/common/MartyLink';
import Review from 'components/reviews/Review';
import MostHelpfulReviews from 'components/reviews/MostHelpfulReviews';
import ReviewSort from 'components/reviews/ReviewSort';
import { toThousandsSeparator } from 'helpers/NumberFormats';
import { buildSeoProductUrl } from 'helpers/SeoUrlBuilder';
import marketplace from 'cfg/marketplace.json';
import { pluralize } from 'helpers';

import css from 'styles/components/productdetail/reviewPreview.scss';

export class ReviewPreview extends Component {
  state = {
    scrollInProgress: false
  };

  getAddReviewUrl = ({ productId, colorId }) => `/product/review/add/${productId}${colorId ? `/color/${colorId}` : ''}`;

  handleReviewUpvoteClick = reviewId => {
    const { product, params: { colorId }, upvoteReview, reviewData: { loadingReviews }, trackEvent } = this.props;
    if (!loadingReviews.includes(reviewId)) {
      const returnTo = buildSeoProductUrl(product, colorId);
      upvoteReview(reviewId, returnTo);
      trackEvent('TE_PDP_RATEREVIEWHELPFUL');
    }
  };

  handleSortReviewsClick = orderBy => {
    const { fetchProductReviews, params: { productId }, reviewData, trackEvent } = this.props;
    if (reviewData.orderBy === orderBy) {
      return;
    }
    fetchProductReviews(productId, 1, 0, false, orderBy);
    trackEvent('TE_PDP_SORTREVIEWBYNEW');
  };

  makeBrandProductName = () => {
    const { brandProductName } = this.props.product;
    return parseAndCleanHtmlToReact(brandProductName);
  };

  makeProductReviewContainerUrl(productId, orderBy) {
    return `/product/review/${productId}${orderBy ? `/page/1/orderBy/${orderBy}` : ''}`;
  }

  makeReviewsHeading = () => (
    <>
      <h2 className={css.reviewsHeading}>Customer {pluralize('Review', this.props.reviewData.reviews?.length)}</h2>
      <div className={css.reviewsBrandProductName}>{this.makeBrandProductName()}</div>
    </>
  );

  makeReviewsList = count => {
    const {
      product: { productName },
      reviewData: { reviews, submittedReviews, loadingReviews },
      onReviewMediaClick
    } = this.props;

    return reviews.slice(0, count).map((review, i) => (
      <div key={review.id} className={cn(css.reviewWrapper, { [css.lastReviewWrapper]: i === count - 1 })}>
        <Review
          review={review}
          productName={productName}
          loadingReviews={loadingReviews}
          submittedReviews={submittedReviews}
          onReviewUpvoteClick={this.handleReviewUpvoteClick}
          onReviewMediaClick={onReviewMediaClick} />
      </div>
    ));
  };

  render() {
    const {
      context: { testId },
      props: {
        product: {
          reviewCount = 0,
          productName, reviewSummary
        },
        reviewData: {
          reviews: productReviews,
          isLoading, loadingReviews, orderBy, submittedReviews
        },
        params: { productId },
        params, showReviews, onReviewMediaClick
      },
      handleReviewUpvoteClick, makeBrandProductName, makeReviewsHeading
    } = this;

    if (!showReviews) {
      return null;
    }
    return (
      <div className={css.container}>
        {MostHelpfulReviews.shouldRender(reviewSummary) && (
          <div className={css.mostHelpfulReviewsHeadingWrapper}>
            <h2 className={css.mostHelpfulReviewsHeading}>What Customers Are Saying</h2>
            <div className={css.mostHelpfulReviewsBrandProductName}>{makeBrandProductName()}</div>
          </div>
        )}
        {productReviews && productReviews.length ? (
          <>
            <MostHelpfulReviews
              reviewSummary={reviewSummary}
              data-test-id={testId('helpfulReviews')}
              loadingReviews={loadingReviews}
              onReviewMediaClick={onReviewMediaClick}
              onReviewUpvoteClick={handleReviewUpvoteClick}
              productName={productName}
              submittedReviews={submittedReviews}
              isPdp={true}/>
            {isLoading ? <Loader/> : (
              <>
                <div className={css.reviewsTitleContainer}>
                  {makeReviewsHeading()}
                  <div className={css.writeAReviewLink}>
                    <MartyLink
                      to={this.getAddReviewUrl(params)}
                      data-te="TE_PDP_WRITEREVIEW"
                      data-test-id={testId('writeAReviewButton')}>
                      Write a review
                    </MartyLink>
                  </div>
                  <div data-test-id={testId('sortOptions')}>
                    <ReviewSort orderBy={orderBy} onSortReviewsClick={this.handleSortReviewsClick}/>
                  </div>
                </div>
                {this.makeReviewsList(5)}
                <div className={css.readAdditionalReviewsWrapper}>
                  {reviewCount > 5 && (
                    <MartyLink
                      to={this.makeProductReviewContainerUrl(productId, orderBy)}
                      className={css.readAdditionalReviews}
                      data-test-id={testId('readAdditionalReviews')}>
                      <span>Read Additional {toThousandsSeparator(reviewCount - 5)} Customer Reviews</span>
                    </MartyLink>
                  )}
                </div>
              </>
            )}
          </>
        ) : null}
        {productReviews && !productReviews.length ? (
          <div className={css.noReviewsContainer}>
            {makeReviewsHeading()}
            <p data-test-id={testId('zeroReviewsText')} className={css.noReviewsText}>This product currently has 0 reviews.</p>
            <MartyLink
              to={this.getAddReviewUrl(params)}
              className={css.writeAReviewLink}
              data-te="TE_PDP_WRITEREVIEW"
              data-test-id={testId('writeAReviewButton')}>
              Write a review
            </MartyLink>
          </div>
        ) : null}
      </div>
    );
  }
}

ReviewPreview.propTypes = {
  product: PropTypes.object.isRequired,
  showReviews: PropTypes.bool,
  params: PropTypes.object.isRequired,
  returnTo: PropTypes.string.isRequired,
  reviewData: PropTypes.object.isRequired,
  authObj: PropTypes.object,
  onReviewMediaClick: PropTypes.func,
  trackEvent: PropTypes.func
};

ReviewPreview.contextTypes = {
  testId: PropTypes.func
};

ReviewPreview.defaultProps = {
  trackEvent
};

function mapStateToProps(state) {
  const {
    product: { authObj, detail, reviewData }
  } = state;
  const { features: { showReviews } } = marketplace;
  return {
    authObj,
    product: detail,
    showReviews,
    reviewData,
    returnTo: buildSeoProductUrl(detail)
  };
}

const mapDispatchToProps = {
  fetchProductReviews,
  upvoteReview
};

export default connect(mapStateToProps, mapDispatchToProps)(ReviewPreview);
